import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { isFieldSurface } from '../../lib/inspection-surface';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { fleetStatusSkin } from '../../screens/trucking/FleetRegistryScreen';
import {
  type TripInspectionDefect, type TripInspectionDetail,
  CERTIFICATION_LABEL, ITEM_RESULT_LABEL, UNIT_ROLE_LABEL, isTripOffice, personName, reportOutcome, requireData, tripErrorMessage, tripTypeLabel,
} from '../../lib/trip-inspections';
import { OutcomeBadge } from './TripInspectionsScreen';
import TripPageChrome from './TripPageChrome';

/**
 * One trip inspection, read-only, with the office's repair certification.
 *
 * A filed report is never edited (api#855 has no PUT); the only write here is
 * certifying a defect, which is the office's — and never the reporting
 * driver's, which the API enforces and this page does not try to predict: a
 * refusal arrives as the API's own sentence. Certifying a unit's last open
 * major defect returns it to service on the server; the page reports the
 * status the server answered with rather than assuming one.
 *
 * A PRE-TRIP lists the post-trips it acknowledged, per unit (api 0437). The
 * office links through to each; a driver may not open another driver's report
 * (the API 404s it), so the field page states the date without a link. A
 * pre-trip with none says "no review recorded" — true both of a unit with no
 * post-trip at the time and of a report filed before reviews existed, and it
 * claims neither.
 */
export default function TripInspectionDetailPage() {
  const { id = '' } = useParams();
  const { pathname } = useLocation();
  const onField = isFieldSurface(pathname);
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const canCertify = !onField && isTripOffice(user?.role);
  const listPath = onField ? '/field/trip-inspections' : '/trucking/trip-inspections';

  const query = useQuery<TripInspectionDetail>({
    queryKey: ['trip-inspections', 'detail', id],
    queryFn: () => api.trucking.tripInspections.get(id).then((r) => requireData(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const r = query.data;
  const notFound = (query.error as any)?.response?.status === 404;

  return (
    <TripPageChrome title="Trip Inspection" eyebrow="Fleet safety" onBack={() => navigate(listPath)}>
      {query.isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="size-6 animate-spin text-fieldiq-muted" /></div>
      ) : notFound ? (
        <p className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4 text-sm text-fieldiq-text">This trip inspection does not exist, or is not yours to open.</p>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this trip inspection" />
      ) : r ? (
        <Report r={r} timeZone={timeZone} canCertify={canCertify} linkReviews={!onField} />
      ) : null}
    </TripPageChrome>
  );
}

function Report({ r, timeZone, canCertify, linkReviews }: { r: TripInspectionDetail; timeZone: string | null; canCertify: boolean; linkReviews: boolean }) {
  const cl = r.checklist;
  const labelOf = (key: string) => cl?.items.find((i) => i.key === key)?.label ?? key;
  const sevLabel = (s: 'minor' | 'major') => cl?.severity_labels[s] ?? s;
  const unitOf = (unitId: string) => r.units.find((u) => u.id === unitId);

  return (
    <div className="space-y-4" data-testid="trip-inspection-detail">
      <section className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-fieldiq-muted">{cl ? `${cl.title} · ${cl.source}` : r.checklist_version}</p>
          <OutcomeBadge outcome={reportOutcome(r)} />
        </div>
        <dl className="mt-3 grid gap-3 sm:grid-cols-3">
          <Field label="Inspection">{tripTypeLabel(r.inspection_type)}</Field>
          <Field label="Inspected">{formatInstant(r.inspected_at, timeZone) ?? '—'}</Field>
          <Field label="Location">{r.location || '—'}</Field>
          <Field label="Driver">{personName(r.driver_first_name, r.driver_last_name)}</Field>
          <Field label="Inspector (printed name)">{r.inspector_name || '—'}</Field>
          <Field label="Odometer">{r.odometer != null ? `${r.odometer} ${r.odometer_unit ?? ''}`.trim() : '—'}</Field>
          <Field label="Signature">{r.signature_key ? 'Signed in the app' : r.signed_on_paper ? 'Signed on the paper report' : '—'}</Field>
        </dl>
        {r.entered_by_office && (
          <p data-testid="trip-entered-by-office" className="mt-3 inline-flex rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] text-fieldiq-text">
            Entered by office · {personName(r.entered_by_first_name, r.entered_by_last_name)}
          </p>
        )}
      </section>

      <section className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
        <h2 className="mb-2 text-sm font-semibold text-fieldiq-text">Units</h2>
        <ul className="space-y-2">
          {r.units.map((u) => {
            const skin = fleetStatusSkin(u.current_status);
            return (
              <li key={u.id} className="flex flex-wrap items-center gap-2 text-sm text-fieldiq-text">
                <span className="font-bold">{u.unit_number}</span>
                <span className="text-fieldiq-muted">{UNIT_ROLE_LABEL[u.unit_role] ?? '—'}{u.plate ? ` · ${u.plate}` : ''}</span>
                <span className={`inline-flex rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink ${skin.fill}`}>
                  Now: {skin.label}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {r.inspection_type === 'pre_trip' && (
        <section data-testid="trip-reviews" className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
          <h2 className="mb-2 text-sm font-semibold text-fieldiq-text">Last post-trip reviewed</h2>
          {(r.reviewed_post_trips ?? []).length === 0 ? (
            <p className="text-sm text-fieldiq-text">No post-trip review recorded on this pre-trip.</p>
          ) : (
            <ul className="space-y-1">
              {(r.reviewed_post_trips ?? []).map((v) => {
                const unit = r.units.find((u) => u.fleet_id === v.fleet_id)?.unit_number ?? '—';
                const when = formatInstant(v.reviewed_inspected_at, timeZone) ?? '—';
                return (
                  <li key={v.fleet_id} className="text-sm text-fieldiq-text">
                    <span className="font-bold">{unit}</span>:{' '}
                    {linkReviews
                      ? <Link to={`/trucking/trip-inspections/${v.reviewed_inspection_id}`} className="font-semibold underline">post-trip of {when}</Link>
                      : <>post-trip of {when}</>}
                    {' · '}{v.reviewed_no_defects ? 'no defects' : 'had defects'}
                    <span className="text-fieldiq-muted"> · acknowledged {formatInstant(v.acknowledged_at, timeZone) ?? '—'}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      <section className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
        <h2 className="mb-2 text-sm font-semibold text-fieldiq-text">Items</h2>
        <ol className="space-y-2">
          {r.item_results.map((ir, idx) => {
            const ds = r.defects.filter((d) => d.item_key === ir.item);
            return (
              <li key={ir.item} className="rounded-lg border border-fieldiq-border bg-fieldiq-dark p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-fieldiq-text">{idx + 1}. {labelOf(ir.item)}</span>
                  <span className="text-xs font-semibold text-fieldiq-muted">{ITEM_RESULT_LABEL[ir.result] ?? '—'}</span>
                </div>
                {ds.map((d) => (
                  <DefectRow key={d.id} reportId={r.id} d={d} sevLabel={sevLabel(d.severity)} unitNumber={unitOf(d.unit_id)?.unit_number ?? '—'} canCertify={canCertify} timeZone={timeZone} />
                ))}
              </li>
            );
          })}
        </ol>
      </section>

      {r.remarks && (
        <section className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
          <h2 className="mb-1 text-sm font-semibold text-fieldiq-text">Remarks</h2>
          <p className="whitespace-pre-wrap text-sm text-fieldiq-text">{r.remarks}</p>
        </section>
      )}
    </div>
  );
}

function DefectRow({ reportId, d, sevLabel, unitNumber, canCertify, timeZone }: {
  reportId: string; d: TripInspectionDefect; sevLabel: string; unitNumber: string; canCertify: boolean; timeZone: string | null;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<'repaired' | 'repair_unnecessary' | ''>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const certify = useMutation({
    mutationFn: () => api.trucking.tripInspections.certify(reportId, d.id, {
      certification: choice,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    }).then((res) => requireData(res.data)),
    onSuccess: async (data) => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['trip-inspections'] });
      await queryClient.invalidateQueries({ queryKey: ['trucking-fleet'] });
      toast.success(`Certified. ${unitNumber} is now ${fleetStatusSkin(data?.unit_status ?? null).label.toLowerCase()}.`);
    },
    onError: (err) => setError(tripErrorMessage(err, 'The certification was not saved.')),
  });

  return (
    <div data-testid={`trip-defect-${d.id}`} className="mt-2 rounded-lg border border-fieldiq-border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink ${d.severity === 'major' ? 'bg-fi-signal-red' : 'bg-fi-signal-amber'}`}>{sevLabel}</span>
        <span className="text-xs text-fieldiq-muted">{unitNumber}</span>
      </div>
      <p className="mt-1 text-sm text-fieldiq-text">{d.defect_text}</p>
      {d.notes && <p className="text-xs text-fieldiq-muted">{d.notes}</p>}
      {d.certification ? (
        <p className="mt-1 text-xs text-fieldiq-text">
          {CERTIFICATION_LABEL[d.certification] ?? '—'} · {personName(d.certified_by_first_name, d.certified_by_last_name)} · {formatInstant(d.certified_at, timeZone) ?? '—'}
          {d.certification_notes ? ` · ${d.certification_notes}` : ''}
        </p>
      ) : (
        <p className="mt-1 text-xs font-semibold text-fieldiq-text">Not certified</p>
      )}
      {!d.certification && canCertify && !open && (
        <button type="button" onClick={() => { setOpen(true); setError(null); }} className="mt-2 text-xs font-semibold text-fieldiq-accent-signal">Certify repair</button>
      )}
      {open && (
        <div className="mt-2 space-y-2">
          <div role="radiogroup" aria-label="Certification" className="flex flex-wrap gap-3 text-sm text-fieldiq-text">
            {(['repaired', 'repair_unnecessary'] as const).map((c) => (
              <label key={c} className="flex items-center gap-1.5">
                <input type="radio" name={`cert-${d.id}`} checked={choice === c} onChange={() => setChoice(c)} /> {CERTIFICATION_LABEL[c]}
              </label>
            ))}
          </div>
          <input aria-label="Certification note" className="w-full rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (optional)" />
          {error && <p role="alert" className="text-xs text-fieldiq-text">{error}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={!choice || certify.isPending} onClick={() => { setError(null); certify.mutate(); }} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">Confirm</button>
            <button type="button" onClick={() => setOpen(false)} className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[0.65rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-fieldiq-text">{children}</dd>
    </div>
  );
}
